var __makeTemplateObject = this && this.__makeTemplateObject || function (cooked, raw) {
  if (Object.defineProperty) {
    Object.defineProperty(cooked, "raw", {
      value: raw
    });
  } else {
    cooked.raw = raw;
  }
  return cooked;
};
import React, { useCallback } from 'react';
import styled, { css } from 'styled-components';
import { breakpoints } from '@shapeable/theme';
import { classNames } from '@shapeable/utils';
import { animated, to } from '@react-spring/web';
import { useSpringValues } from '../hooks/use-spring-values';
import { useThemeColor } from '../hooks/use-theme-color';
var cls = classNames('curved-line');
export var SvgCurvedLineDefaultProps = {
  animate: true,
  curvature: 20,
  intensity: 1,
  controlPoints: 2,
  // Default to 2 control points (cubic Bezier)
  strokeColor: 'strong',
  strokeWidth: 2,
  strokeOpacity: 1,
  // Default opacity is 1 (fully opaque)
  strokeDasharray: '',
  // Default to solid line
  t: 1,
  arrowSize: 0 // Default arrowhead size
  // springConfig: { tension: 120, friction: 14, mass: 1, },
};
// -------- Styles -------->
var ContainerStyles = breakpoints({
  base: css(templateObject_1 || (templateObject_1 = __makeTemplateObject(["\n  "], ["\n  "])))
});
var LineStyles = breakpoints({
  base: css(templateObject_2 || (templateObject_2 = __makeTemplateObject(["\n    \n  "], ["\n    \n  "])))
});
var ArrowHeadStyles = breakpoints({
  base: css(templateObject_3 || (templateObject_3 = __makeTemplateObject(["\n    \n  "], ["\n    \n  "])))
});
// -------- Components -------->
var My = {
  Container: styled(animated.svg)(templateObject_4 || (templateObject_4 = __makeTemplateObject(["", ""], ["", ""])), ContainerStyles),
  Line: styled(animated.path)(templateObject_5 || (templateObject_5 = __makeTemplateObject(["", ""], ["", ""])), LineStyles),
  ArrowHead: styled(animated.polygon)(templateObject_6 || (templateObject_6 = __makeTemplateObject(["", ""], ["", ""])), ArrowHeadStyles)
};
export var SvgCurvedLine = function (props) {
  var className = props.className,
    x1 = props.x1,
    y1 = props.y1,
    x2 = props.x2,
    y2 = props.y2,
    curvature = props.curvature,
    intensity = props.intensity,
    controlPoints = props.controlPoints,
    t = props.t,
    strokeWidth = props.strokeWidth,
    strokeOpacity = props.strokeOpacity,
    strokeDasharray = props.strokeDasharray,
    arrowSize = props.arrowSize,
    centerX = props.centerX,
    centerY = props.centerY;
  var values = useSpringValues(props);
  var strokeColor = useThemeColor(props.strokeColor);
  var lineCalc = useCallback(function (x1, y1, x2, y2, curvature, t) {
    // Midpoint between the start and end points
    var midX = (x1 + x2) / 2;
    var midY = (y1 + y2) / 2;
    var controlPoint1X = midX;
    var controlPoint1Y = midY;
    var controlPoint2X = midX;
    var controlPoint2Y = midY;
    if (centerX !== undefined && centerY !== undefined) {
      // If center is provided, bias control points toward the center
      var vectorToCenterX = centerX - midX;
      var vectorToCenterY = centerY - midY;
      // Normalize the vector and scale by the curvature and intensity
      var distanceToCenter = Math.sqrt(Math.pow(vectorToCenterX, 2) + Math.pow(vectorToCenterY, 2));
      var normalizedVectorX = distanceToCenter === 0 ? 1 : vectorToCenterX / distanceToCenter * intensity;
      var normalizedVectorY = distanceToCenter === 0 ? 1 : vectorToCenterY / distanceToCenter * intensity;
      // Curvature factor based on prop (0 is straight, 100 is maximum curvature)
      var curvatureFactor = Math.sin(Math.PI / 2 * (curvature / 100)) * distanceToCenter / 2;
      controlPoint1X = midX + normalizedVectorX * curvatureFactor;
      controlPoint1Y = midY + normalizedVectorY * curvatureFactor;
      controlPoint2X = midX - normalizedVectorX * curvatureFactor;
      controlPoint2Y = midY - normalizedVectorY * curvatureFactor;
    } else {
      // If no center is provided, use standard curvature behavior without center bias
      var curvatureFactor = Math.sin(Math.PI / 2 * (curvature / 100)) * intensity;
      controlPoint1X = midX;
      controlPoint1Y = midY - curvatureFactor * (y2 - y1) / 2;
      controlPoint2X = midX;
      controlPoint2Y = midY + curvatureFactor * (y2 - y1) / 2;
    }
    // Function implementing de Casteljau's algorithm to get partial curve
    var deCasteljau = function (t) {
      if (controlPoints === 1) {
        var p0 = {
          x: x1,
          y: y1
        };
        var p1 = {
          x: controlPoint1X,
          y: controlPoint1Y
        };
        var p2 = {
          x: x2,
          y: y2
        };
        var q0 = {
          x: (1 - t) * p0.x + t * p1.x,
          y: (1 - t) * p0.y + t * p1.y
        };
        var q1 = {
          x: (1 - t) * p1.x + t * p2.x,
          y: (1 - t) * p1.y + t * p2.y
        };
        var r = {
          x: (1 - t) * q0.x + t * q1.x,
          y: (1 - t) * q0.y + t * q1.y
        };
        return {
          p0: p0,
          q0: q0,
          r: r
        };
      } else if (controlPoints === 2) {
        var p0 = {
          x: x1,
          y: y1
        };
        var p1 = {
          x: controlPoint1X,
          y: controlPoint1Y
        };
        var p2 = {
          x: controlPoint2X,
          y: controlPoint2Y
        };
        var p3 = {
          x: x2,
          y: y2
        };
        var q0 = {
          x: (1 - t) * p0.x + t * p1.x,
          y: (1 - t) * p0.y + t * p1.y
        };
        var q1 = {
          x: (1 - t) * p1.x + t * p2.x,
          y: (1 - t) * p1.y + t * p2.y
        };
        var q2 = {
          x: (1 - t) * p2.x + t * p3.x,
          y: (1 - t) * p2.y + t * p3.y
        };
        var r0 = {
          x: (1 - t) * q0.x + t * q1.x,
          y: (1 - t) * q0.y + t * q1.y
        };
        var r1 = {
          x: (1 - t) * q1.x + t * q2.x,
          y: (1 - t) * q1.y + t * q2.y
        };
        var s = {
          x: (1 - t) * r0.x + t * r1.x,
          y: (1 - t) * r0.y + t * r1.y
        };
        return {
          p0: p0,
          q0: q0,
          r0: r0,
          s: s
        };
      }
      return {};
    };
    var partialCurve = deCasteljau(t);
    var pathData = '';
    if (t === 0) {
      pathData = '';
    } else if (controlPoints === 1) {
      pathData = "M ".concat(partialCurve.p0.x, ",").concat(partialCurve.p0.y, " Q ").concat(partialCurve.q0.x, ",").concat(partialCurve.q0.y, " ").concat(partialCurve.r.x, ",").concat(partialCurve.r.y);
    } else if (controlPoints === 2) {
      pathData = "M ".concat(partialCurve.p0.x, ",").concat(partialCurve.p0.y, " C ").concat(partialCurve.q0.x, ",").concat(partialCurve.q0.y, " ").concat(partialCurve.r0.x, ",").concat(partialCurve.r0.y, " ").concat(partialCurve.s.x, ",").concat(partialCurve.s.y);
    }
    var getBezierAngle = function (t) {
      var c1 = deCasteljau(t);
      var c2 = deCasteljau(t + 0.01);
      var p1 = c1.s || c1.r;
      var p2 = c1.s || c2.r;
      var angle = Math.atan2(p2.y - p1.y, p2.x - p1.x);
      return angle * 180 / Math.PI;
    };
    // const arrowAngle = getBezierAngle(t);
    // Function to calculate Bézier curve point at t (0 <= t <= 1)
    var getBezierPoint = function (t) {
      if (controlPoints === 1) {
        // Quadratic Bézier curve point calculation
        var x = (1 - t) * (1 - t) * x1 + 2 * (1 - t) * t * controlPoint1X + t * t * x2;
        var y = (1 - t) * (1 - t) * y1 + 2 * (1 - t) * t * controlPoint1Y + t * t * y2;
        return {
          x: x,
          y: y
        };
      } else if (controlPoints === 2) {
        // Cubic Bézier curve point calculation
        var x = Math.pow(1 - t, 3) * x1 + 3 * Math.pow(1 - t, 2) * t * controlPoint1X + 3 * (1 - t) * t * t * controlPoint2X + Math.pow(t, 3) * x2;
        var y = Math.pow(1 - t, 3) * y1 + 3 * Math.pow(1 - t, 2) * t * controlPoint1Y + 3 * (1 - t) * t * t * controlPoint2Y + Math.pow(t, 3) * y2;
        return {
          x: x,
          y: y
        };
      }
      return {
        x: 0,
        y: 0
      };
    };
    // // Midpoint of the curve (t = 0.5)
    // const midPoint = getBezierPoint(0.5);
    // // Angle of the curve at the midpoint
    // const arrowAngle = getBezierAngle(0.5);
    return {
      pathData: pathData
      // arrowAngle,
      // midPoint,
    };
  }, [x1, y1, x2, y2, curvature, t]);
  var pathData = function (x1, y1, x2, y2, curvature, t) {
    return lineCalc(x1, y1, x2, y2, curvature, t).pathData;
  };
  return React.createElement(My.Container, {
    className: cls.name(className),
    style: {
      overflow: 'visible'
    }
  }, React.createElement(My.Line, {
    d: to([values.x1, values.y1, values.x2, values.y2, values.curvature, values.t], pathData),
    stroke: strokeColor,
    strokeWidth: strokeWidth,
    strokeOpacity: strokeOpacity,
    strokeDasharray: strokeDasharray,
    fill: "none"
  }));
};
SvgCurvedLine.cls = cls;
SvgCurvedLine.defaultProps = SvgCurvedLineDefaultProps;
var templateObject_1, templateObject_2, templateObject_3, templateObject_4, templateObject_5, templateObject_6;